import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import userService, { type SignInReq } from "@/api/services/userService";

import { toast } from "sonner";
import type { UserInfo, UserToken } from "#/entity";
import { StorageEnum } from "#/enum";
import { useNotificationStore } from "@/store/notificationStore";

const HOMEPAGE = import.meta.env.VITE_APP_HOMEPAGE || "/dashboard/home";

type UserStore = {
	userInfo: Partial<UserInfo>;
	userToken: UserToken;
	creditsRemaining: number | null;
	totalCreditsAllocated: number | null;
	daysRemaining: number | null;
	trialEndTime: number | null;
	billingCycleEnd: string | null;
	extendDaysSubmitted: boolean;
	tier: string | null;
	featureAccess: Record<string, boolean>;
	tierId: number | null;
	resetInterval: string | null;
	isFetchingCredits: boolean;
	zeroModalOpen: boolean;
	remainingModalOpen: boolean;
	insufficientModalOpen: boolean;
	hasInsufficientCredits: boolean;
	// 使用 actions 命名空间来存放所有的 action
	actions: {
		setUserInfo: (userInfo: UserInfo) => void;
		setUserToken: (token: UserToken) => void;
		clearUserInfoAndToken: () => void;
		setCreditsRemaining: (credits: number | null) => void;
		setTotalCreditsAllocated: (credits: number | null) => void;
		setDaysRemaining: (days: number | null) => void;
		setTrialEndTime: (time: number | null) => void;
		setBillingCycleEnd: (date: string | null) => void;
		setExtendDaysSubmitted: (value: boolean) => void;
		setTier: (tier: string | null) => void;
		setFeatureAccess: (access: Record<string, boolean>) => void;
		setTierId: (tierId: number | null) => void;
		setResetInterval: (interval: string | null) => void;
		setZeroModalOpen: (open: boolean) => void;
		setRemainingModalOpen: (open: boolean) => void;
		setInsufficientModalOpen: (open: boolean) => void;
		setHasInsufficientCredits: (hasIt: boolean) => void;
		refreshCredits: () => Promise<void>;
	};
};

const useUserStore = create<UserStore>()(
	persist(
		(set, get) => ({
			userInfo: {},
			userToken: {},
			creditsRemaining: null,
			totalCreditsAllocated: null,
			daysRemaining: null,
			trialEndTime: null,
			billingCycleEnd: null,
			extendDaysSubmitted: false,
			tier: null,
			featureAccess: {},
			tierId: null,
			resetInterval: null,
			isFetchingCredits: true,
			zeroModalOpen: false,
			remainingModalOpen: false,
			insufficientModalOpen: false,
			hasInsufficientCredits: false,
			actions: {
				setUserInfo: (userInfo) => {
					set({ userInfo });
				},
				setUserToken: (userToken) => {
					set({ userToken });
				},
				clearUserInfoAndToken() {
					try {
						useUserStore.persist.clearStorage();
					} catch (e) {}
					localStorage.clear();
					sessionStorage.clear();
					try {
						useNotificationStore.getState().actions.clearNotifications();
						useNotificationStore.persist.clearStorage();
					} catch (e) {}
					set({
						userInfo: {},
						userToken: {},
						creditsRemaining: null,
						totalCreditsAllocated: null,
						daysRemaining: null,
						trialEndTime: null,
						billingCycleEnd: null,
						extendDaysSubmitted: false,
						tier: null,
						featureAccess: {},
						tierId: null,
						resetInterval: null,
						isFetchingCredits: false,
						zeroModalOpen: false,
						remainingModalOpen: false,
						insufficientModalOpen: false,
						hasInsufficientCredits: false,
					});
				},
				setCreditsRemaining: (credits) => {
					set({ creditsRemaining: credits });
				},
				setTotalCreditsAllocated: (credits) => {
					set({ totalCreditsAllocated: credits });
				},
				setDaysRemaining: (days) => {
					set({ daysRemaining: days });
				},
				setTrialEndTime: (time) => {
					set({ trialEndTime: time });
				},
				setBillingCycleEnd: (date) => {
					set({ billingCycleEnd: date });
				},
				setExtendDaysSubmitted: (value: boolean) => {
					set({ extendDaysSubmitted: value });
				},
				setTier: (tier: string | null) => {
					set({ tier: tier ? tier.toLowerCase() : null });
				},
				setFeatureAccess: (access: Record<string, boolean>) => {
					set({ featureAccess: access });
				},
				setTierId: (tierId: number | null) => {
					set({ tierId });
				},
				setResetInterval: (interval: string | null) => {
					set({ resetInterval: interval });
				},
				setZeroModalOpen: (open: boolean) => {
					set({ zeroModalOpen: open });
				},
				setRemainingModalOpen: (open: boolean) => {
					set({ remainingModalOpen: open });
				},
				setInsufficientModalOpen: (open: boolean) => {
					set({ insufficientModalOpen: open });
				},
				setHasInsufficientCredits: (hasIt: boolean) => {
					set({ hasInsufficientCredits: hasIt });
				},
				refreshCredits: async () => {
					const state = get();
					// The user ID might be stored directly in localStorage or under user_id in userInfo
					const localUserId = localStorage.getItem('userId');
					const id = state.userInfo?.id || (state.userInfo as any)?.user_id || localUserId;
					const email = state.userInfo?.email;

					set({ isFetchingCredits: true });

					if (!id && !email) {
						set({ isFetchingCredits: false });
						return;
					}

					try {
						const { VITE_AGENT_URL, VITE_APP_URL } = import.meta.env;
						const timestamp = Date.now();
						const res = await fetch(`${VITE_AGENT_URL}/api/v1/user/credits-status?_t=${timestamp}`, {
							headers: { 
								'accept': 'application/json', 
								'x-user-id': String(id || email || 'test_user_123'),
								'Cache-Control': 'no-cache, no-store, must-revalidate',
								'Pragma': 'no-cache'
							},
							cache: 'no-store'
						});
						if (res.ok) {
							const data = await res.json();
							const agentRemaining = data.credits_remaining !== undefined ? data.credits_remaining : 0;
							
							set({ creditsRemaining: agentRemaining });
							const roleId = localStorage.getItem("roleId") || localStorage.getItem("role_id");
							if (agentRemaining < 1 && roleId !== "1") {
								set({ hasInsufficientCredits: true, insufficientModalOpen: true });
							} else {
								set({ hasInsufficientCredits: false });
							}
							if (data.total_credits_allocated !== undefined) {
								set({ totalCreditsAllocated: data.total_credits_allocated });
							}
							const days = data.days_remaining ?? data.trial_days_remaining ?? null;
							if (days !== null) set({ daysRemaining: days });
							if (data.reset_interval !== undefined) set({ resetInterval: data.reset_interval });
							
							const userTierId = data.tier_id;
							let userTierStr = data.tier;
							if (!userTierId && !userTierStr) {
								userTierStr = get().userInfo?.tier || "trial";
							}

							try {
								const headers = { 
									'user-id': String(id || localUserId), 
									'Content-Type': 'application/json',
									'Cache-Control': 'no-cache, no-store, must-revalidate',
									'Pragma': 'no-cache'
								};
								const tiersRes = await fetch(`${VITE_APP_URL}/api/tier/tiers?_t=${timestamp}`, { 
									headers,
									cache: 'no-store'
								});
								if (tiersRes.ok) {
									const tiersList = await tiersRes.json();
									let matchingTier;
									if (userTierId) {
										matchingTier = tiersList.find((t: any) => t.tier_id === userTierId);
									} else if (userTierStr) {
										matchingTier = tiersList.find((t: any) => t.tier_key?.toLowerCase() === userTierStr.toLowerCase() || t.tier_name?.toLowerCase() === userTierStr.toLowerCase());
									}
									if (!matchingTier) {
										matchingTier = tiersList.find((t: any) => t.tier_key?.toLowerCase() === "trial");
									}

									if (matchingTier) {
										set({ tier: matchingTier.tier_key?.toLowerCase(), tierId: matchingTier.tier_id });
										const featuresRes = await fetch(`${VITE_APP_URL}/api/tier/tier-features/tier/${matchingTier.tier_id}?_t=${timestamp}`, { 
											headers,
											cache: 'no-store'
										});
										if (featuresRes.ok) {
											const featuresData = await featuresRes.json();
											const accessMap = featuresData.reduce((acc: any, feature: any) => {
												acc[feature.feature_key] = feature.is_allowed;
												return acc;
											}, {});
											set({ featureAccess: accessMap });
										}
									}
								}
							} catch (featuresErr) {
								console.error("Failed to fetch feature access:", featuresErr);
							}
						}
					} catch (err) {
						console.error("Failed to refresh credits:", err);
					} finally {
						set({ isFetchingCredits: false });
					}
				},
			},
		}),
		{
			name: "userStore", // name of the item in the storage (must be unique)
			storage: createJSONStorage(() => localStorage), // (optional) by default, 'localStorage' is used
			partialize: (state) => ({
				[StorageEnum.UserInfo]: state.userInfo,
				[StorageEnum.UserToken]: state.userToken,
				featureAccess: state.featureAccess,
				tier: state.tier,
				tierId: state.tierId,
				creditsRemaining: state.creditsRemaining,
				totalCreditsAllocated: state.totalCreditsAllocated,
				daysRemaining: state.daysRemaining,
			}),
		},
	),
);

export const useUserInfo = () => useUserStore((state) => state.userInfo);
export const useUserToken = () => useUserStore((state) => state.userToken);
export const useUserPermission = () =>
	useUserStore((state) => state.userInfo.permissions);
export const useUserActions = () => useUserStore((state) => state.actions);
export const useCreditsRemaining = () => useUserStore((state) => state.creditsRemaining);
export const useTotalCreditsAllocated = () => useUserStore((state) => state.totalCreditsAllocated);
export const useDaysRemaining = () => useUserStore((state) => state.daysRemaining);
export const useTrialEndTime = () => useUserStore((state) => state.trialEndTime);
export const useBillingCycleEnd = () => useUserStore((state) => state.billingCycleEnd);
export const useExtendDaysSubmitted = () => useUserStore((state) => state.extendDaysSubmitted);
export const useTier = () => useUserStore((state) => state.tier);
export const useTierId = () => useUserStore((state) => state.tierId);
export const useResetInterval = () => useUserStore((state) => state.resetInterval);
export const useFeatureAccess = () => useUserStore((state) => state.featureAccess);
export const useIsFetchingCredits = () => useUserStore((state) => state.isFetchingCredits);
export const useZeroModalOpen = () => useUserStore((state) => state.zeroModalOpen);
export const useRemainingModalOpen = () => useUserStore((state) => state.remainingModalOpen);
export const useInsufficientModalOpen = () => useUserStore((state) => state.insufficientModalOpen);
export const useHasInsufficientCredits = () => useUserStore((state) => state.hasInsufficientCredits);

export const useSignIn = () => {
	const navigate = useNavigate();
	const { setUserToken, setUserInfo, setCreditsRemaining, setTotalCreditsAllocated, setDaysRemaining, setTier, setTierId, setFeatureAccess, refreshCredits} = useUserActions();

	const signInMutation = useMutation({
		mutationFn: userService.signin,
	});

	const signIn = async (data: SignInReq) => {
		try {
			const res = await signInMutation.mutateAsync(data);
			const { user, accessToken, refreshToken } = res;
			localStorage.removeItem("pending_payment");
			sessionStorage.removeItem("active_transaction_id");
			setUserToken({ accessToken, refreshToken });
			setUserInfo(user);
			setCreditsRemaining(null);
			setTotalCreditsAllocated(null);
			setDaysRemaining(null);
			setFeatureAccess({}); // Reset features to fail-closed state before fetching
			setTier(user?.tier || null);
			setTierId(user?.tier_id || null);

			const { addNotification } = useNotificationStore.getState().actions;

			// Dispatch login notification
			addNotification({
				type: 'system',
				title: 'Login Successful',
				message: `Welcome back, ${user.name || user.email}! You have logged in successfully.`,
			});

			// Fetch credits after login
			try {
				await refreshCredits();
				
				const { creditsRemaining } = useUserStore.getState();
				const roleId = localStorage.getItem("roleId") || localStorage.getItem("role_id");
				// Credits notification & restriction check
				if (creditsRemaining !== null && creditsRemaining !== undefined) {
					if (creditsRemaining < 1 && roleId !== "1") {
						useUserStore.getState().actions.setHasInsufficientCredits(true);
						useUserStore.getState().actions.setInsufficientModalOpen(true);
						addNotification({
							type: 'credits',
							title: 'Insufficient Credits',
							message: 'You have less than 1 credit remaining. Please upgrade your plan to continue using tools.',
						});
					} else if (creditsRemaining <= 1) {
						addNotification({
							type: 'credits',
							title: 'Low Credits Warning',
							message: `You have only ${creditsRemaining} credit${creditsRemaining === 1 ? '' : 's'} remaining. Consider upgrading your plan.`,
						});
					}
				}
			} catch (err) {
				console.error("Failed to fetch credits during sign in", err);
			}

			navigate(HOMEPAGE, { replace: true });
			toast.success("Sign in success!");
		} catch (err) {
			toast.error(err.message, {
				position: "top-center",
			});
		}
	};

	return signIn;
};

export { useUserStore };
export default useUserStore;
